import { ComponentType } from '@angular/cdk/overlay';
import { inject, Injector, Injectable, OnDestroy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription, tap } from 'rxjs';
import { distinctUntilChanged, pairwise } from 'rxjs/operators';
import { SessionStore } from '../stores/session.store';
import { ExtendSessionDialogComponent } from '../../core/dialogs/extend-session-dialog/extend-session-dialog.component';

/**
 * Dialog service handles opening all dialogs in the application. All dialog openings
 * return an observable on completion. Tracks open dialog refs and closes them when
 * the user is no longer authenticated.
 */
@Injectable({
  providedIn: 'root',
})
export class DialogService implements OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly sessionStore = inject(SessionStore);
  private readonly injector = inject(Injector);

  /** List of all currently opened dialog references */
  private openDialogRefs: MatDialogRef<unknown>[] = [];

  /** Subscription to session (auth state) */
  private sessionSubscription: Subscription | null = null;

  constructor() {
    const session$ = toObservable(this.sessionStore.currentSession, {
      injector: this.injector,
    });

    this.sessionSubscription = session$
      .pipe(distinctUntilChanged(), pairwise())
      .subscribe(([prev, curr]) => {
        // When session becomes null after being defined, close all dialogs
        if (prev != undefined && curr === null && this.openDialogRefs.length > 0) {
          this.openDialogRefs.forEach((ref) => ref.close());
          this.openDialogRefs = [];
        }
      });
  }

  /**
   * Opens a dialog component and tracks the reference for session cleanup.
   * @return Observable that emits the dialog result when it closes.
   */
  open<T, D = unknown, R = unknown>(
    component: ComponentType<T>,
    config?: MatDialogConfig<D>,
  ): Observable<R> {
    const dialogRef = this.dialog.open(component, config);
    return this.trackDialogRef<T, R>(dialogRef);
  }

  /**
   * Opens the Extend Session dialog. User must choose Extend or Log Out.
   * @return Observable that emits 'extend' or 'logout' when the dialog closes.
   */
  openExtendSession(): Observable<'extend' | 'logout'> {
    return this.open(ExtendSessionDialogComponent, {
      width: '400px',
      disableClose: true,
      panelClass: 'extend-session-dialog-container',
    });
  }

  /**
   * Helper method to track dialog references and return observable with cleanup
   */
  private trackDialogRef<T, R>(dialogRef: MatDialogRef<T>): Observable<R> {
    this.openDialogRefs.push(dialogRef as MatDialogRef<unknown>);
    return dialogRef.afterClosed().pipe(
      tap(() => {
        const index = this.openDialogRefs.indexOf(dialogRef as MatDialogRef<unknown>);
        if (index > -1) {
          this.openDialogRefs.splice(index, 1);
        }
      }),
    ) as Observable<R>;
  }

  ngOnDestroy(): void {
    this.sessionSubscription?.unsubscribe();
  }
}
